// Single sign-on provider for the STAFF softphone (OIDC, authorization code +
// PKCE S256). Initialized lazily from the /staff surface only, so the public
// guest caller never contacts the identity provider.
//
// The app is hash-routed, so the provider runs in `responseMode: 'query'`: the
// authorization response comes back in the query string and does not collide
// with the router's fragment. `initSso()` is idempotent (safe to call on every
// mount of the login screen). Naming is generic; the underlying library is an
// implementation detail and never surfaces in UI, URLs or storage keys.

import Keycloak from 'keycloak-js'

// Static authentication configuration: this app serves the qr-phone.com facade
// only, so the identity realm is a constant of the product. The only
// per-environment value is the OIDC client id (production vs the dev host).
const AUTH_AUTHORITY = 'https://id.qr-phone.com'
const AUTH_REALM = 'qr-phone'
const AUTH_CLIENT_ID: string = import.meta.env.VITE_AUTH_CLIENT_ID || 'qr-phone-staff'

let provider: Keycloak | null = null
let initPromise: Promise<boolean> | null = null
let tokenListener: ((token: string) => void) | null = null

function appBase(): string {
  return import.meta.env.BASE_URL || '/'
}

function staffLoginUri(): string {
  return `${window.location.origin}${appBase()}#/staff`
}

export function onToken(listener: (token: string) => void): void {
  tokenListener = listener
}

export function initSso(): Promise<boolean> {
  if (initPromise) return initPromise
  provider = new Keycloak({ url: AUTH_AUTHORITY, realm: AUTH_REALM, clientId: AUTH_CLIENT_ID })

  initPromise = provider
    .init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      responseMode: 'query',
      redirectUri: staffLoginUri(),
      silentCheckSsoRedirectUri: `${window.location.origin}${appBase()}silent-check-sso.html`,
      checkLoginIframe: false,
    })
    .then((authenticated) => {
      if (authenticated) {
        // Renew reactively when the token expires...
        provider!.onTokenExpired = () => {
          void ensureValidToken()
        }
        // ...and proactively, which is what actually matters here. A staff
        // member keeps this open all day waiting for calls, and a purely
        // reactive renewal fails them twice over: browsers throttle timers in
        // background tabs so the expiry callback fires late, and the session at
        // the identity service expires on its own after a period without
        // activity. Renewing on a short interval keeps the token fresh and
        // resets that inactivity window, so an open tab never loses its
        // session and a call is never interrupted by a sign-in redirect.
        startProactiveRenewal()
      }
      return Boolean(authenticated)
    })

  return initPromise
}

export function getAccessToken(): string | undefined {
  return provider?.token
}

// How often to consider renewing, and how much validity to guarantee. The
// interval must stay well below both the token lifetime and the inactivity
// window at the identity service.
const RENEWAL_INTERVAL_MS = 60 * 1000
const MIN_VALIDITY_SECONDS = 120

let renewalTimer: ReturnType<typeof setInterval> | null = null
let renewalInFlight: Promise<boolean> | null = null

function startProactiveRenewal(): void {
  if (renewalTimer) clearInterval(renewalTimer)
  renewalTimer = setInterval(() => {
    void ensureValidToken()
  }, RENEWAL_INTERVAL_MS)

  // A tab returning to the foreground may have missed several intervals.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void ensureValidToken()
  })
}

/**
 * Guarantee a token valid for at least the next couple of minutes, renewing it
 * when it is not. Concurrent callers share one renewal, so a burst of requests
 * cannot start a stampede. Returns whether a usable token is available.
 */
export async function ensureValidToken(): Promise<boolean> {
  if (!provider?.authenticated) return false
  if (renewalInFlight) return renewalInFlight

  renewalInFlight = provider
    .updateToken(MIN_VALIDITY_SECONDS)
    .then((refreshed) => {
      if (refreshed && provider?.token && tokenListener) tokenListener(provider.token)
      return true
    })
    .catch(() => {
      // The session is genuinely gone (revoked, or inactive for too long).
      signIn()
      return false
    })
    .finally(() => {
      renewalInFlight = null
    })

  return renewalInFlight
}

// Send an unauthenticated staff member to the hosted sign-in page.
export function signIn(): void {
  provider?.login({ redirectUri: staffLoginUri() })
}

// End the sign-on session (full-page redirect back to the staff entry point).
//
// The provider is initialized by the login screen, and the dashboard can be reached
// WITHOUT that screen ever mounting: the session is restored from storage at boot, so a
// reload landing straight on the dashboard (or an installed window resuming there) has no
// provider. This used to return silently in that case, which read to the staff member as
// "sign out does nothing": local state was cleared, but the sign-on session stayed alive
// and the silent session check signed them straight back in. Initialization is idempotent
// and cheap, so it is simply ensured here before signing out.
export async function signOut(): Promise<void> {
  if (!provider) {
    try {
      await initSso()
    } catch {
      /* ignore: handled by the guard below */
    }
  }
  if (!provider) return
  await provider.logout({ redirectUri: staffLoginUri() })
}
